import {
  ApolloClient,
  NormalizedCacheObject,
  QueryOptions,
  OperationVariables,
} from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { GetClientEntitiesDocument } from "~/graphql/supergraphHooks";
import {
  type GetClientEntitiesQuery,
  type ClientArenaFilter,
  type ClientArenaSort,
} from "~/graphql/supergraphTypes";
import { EntityType, FilterInfo } from "./types";

export const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
): ClientArenaFilter[] | null => {
  if (!filterModel) {
    return null;
  }
  if (filterModel?.filterType) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }
  const queryFilters: ClientArenaFilter[] = [];

  Object.entries(filterModel).forEach((filter) => {
    const field = filter[0];
    const filterInfo = filter[1] as FilterInfo;

    if (filterInfo.conditions) {
      if (filterInfo.operator === "OR") {
        const values = filterInfo.conditions.map((condition) => String(condition.filter));
        queryFilters.push({
          field: field,
          filterValues: values,
        });
      } else if (filterInfo.operator === "AND") {
        filterInfo.conditions.forEach((condition) => {
          queryFilters.push({
            field: field,
            filterValues: [String(condition.filter)],
          });
        });
      } else {
        throw Error("Unexpected operator for multiple conditions");
      }
    } else if (filterInfo.filterType === "set" && filterInfo.values) {
      queryFilters.push({
        field: field,
        filterValues: filterInfo.values.map(String),
      });
    } else if (filterInfo.filterType === "text" && typeof filterInfo.filter === "string") {
      queryFilters.push({
        field: field,
        filterValues: [filterInfo.filter],
      });
    } else if (filterInfo.filterType === "number" && typeof filterInfo.filter === "number") {
      queryFilters.push({
        field: field,
        filterValues: [filterInfo.filter.toString()],
      });
    } else if (filterInfo.filterType === "date" && filterInfo.dateFrom) {
      if (filterInfo.type === "equals" || !filterInfo.type) {
        queryFilters.push({
          field: field,
          filterValues: [filterInfo.dateFrom],
        });
      } else {
        throw Error("Only equals filter type is supported for date filters");
      }
    } else {
      throw Error("Unsupported filter type");
    }
  });
  return queryFilters.length > 0 ? queryFilters : null;
};

export const getSortVars = (
  sortItems: AgGrid.SortModelItem[] | null,
): ClientArenaSort[] | null => {
  if (!sortItems || sortItems.length === 0) {
    return null;
  }
  const querySorts: ClientArenaSort[] = [];
  for (const item of sortItems) {
    querySorts.push({
      field: item.colId,
      sortAscending: item.sort === "asc",
    });
  }
  return querySorts;
};

export const rowsFromQueryEntities = (
  entities: EntityType[] | undefined | null,
): EntityType[] => {
  if (!entities) {
    return [];
  }
  return entities;
};

export interface ClientDataGraphqlDataSourceProps {
  onFiltersChange?: (filters: ClientArenaFilter[] | null) => void;
  onSortsChange?: (sorts: ClientArenaSort[] | null) => void;
}

export class ClientDataGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;
  clientId: string;
  asOfDate: string;
  private onFiltersChange?: (filters: ClientArenaFilter[] | null) => void;
  private onSortsChange?: (sorts: ClientArenaSort[] | null) => void;

  constructor(
    clientId: string,
    props?: ClientDataGraphqlDataSourceProps,
    asOfDate: string = "",
    apolloClient: ApolloClient<NormalizedCacheObject> = client,
  ) {
    this.client = apolloClient;
    this.clientId = clientId;
    this.asOfDate = asOfDate;
    this.onFiltersChange = props?.onFiltersChange;
    this.onSortsChange = props?.onSortsChange;
  }

  setAsOfDate(asOfDate: string): void {
    this.asOfDate = asOfDate;
  }

  public handleError(params: AgGrid.IServerSideGetRowsParams): void {
    params.fail();
  }

  public calculateRowCount(
    filteredCount: number | null | undefined,
    rowDataLength: number,
    startRow: number | null | undefined,
    endRow: number | null | undefined,
  ): number {
    if (typeof filteredCount === "number") {
      return filteredCount;
    }

    if (
      startRow === null ||
      startRow === undefined ||
      endRow === null ||
      endRow === undefined
    ) {
      return rowDataLength;
    }

    if (rowDataLength < endRow - startRow) {
      return startRow + rowDataLength;
    }

    return -1;
  }

  public handleSuccess(
    params: AgGrid.IServerSideGetRowsParams,
    response: GetClientEntitiesQuery,
  ): void {
    if (!response.getClientEntities) {
      this.handleError(params);
      return;
    }

    const { entities, filteredCount } = response.getClientEntities;
    const rowData = rowsFromQueryEntities(entities);
    const startRow = params.request.startRow ?? 0;
    const endRow = params.request.endRow ?? 0;
    const rowCount = this.calculateRowCount(filteredCount, rowData.length, startRow, endRow);

    params.success({
      rowData,
      rowCount,
    });

    // Handle no-rows overlay for server-side row model
    if (rowCount === 0 && startRow === 0) {
      params.api?.showNoRowsOverlay();
    } else if (rowCount > 0) {
      params.api?.hideOverlay();
    }
  }

  public buildQueryOptions(
    params: AgGrid.IServerSideGetRowsParams,
  ): QueryOptions<OperationVariables, GetClientEntitiesQuery> {
    const filters = getFilterVars(params.request.filterModel);
    const sorts = getSortVars(params.request.sortModel);
    const offset = params.request.startRow ?? 0;
    const calculatedLimit = (params.request.endRow ?? 0) - offset;
    const limit = calculatedLimit <= 0 ? 10 : calculatedLimit;

    if (this.onFiltersChange) {
      this.onFiltersChange(filters);
    }

    if (this.onSortsChange) {
      this.onSortsChange(sorts);
    }

    const filtersVar = filters === null ? null : filters;
    const sortsVar = sorts === null ? null : sorts;

    return {
      query: GetClientEntitiesDocument,
      variables: {
        clientId: this.clientId,
        asOfDate: this.asOfDate,
        offset: offset,
        limit: limit,
        filters: filtersVar,
        sorts: sortsVar,
      },

      fetchPolicy: "no-cache",
    };
  }

  public executeQuery(
    params: AgGrid.IServerSideGetRowsParams,
    queryOptions: ReturnType<typeof this.buildQueryOptions>,
  ): Promise<void> {
    return this.client
      .query<GetClientEntitiesQuery>(queryOptions)
      .then((response) => {
        this.handleSuccess(params, response.data);
      })
      .catch((_error) => {
        this.handleError(params);
      });
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const queryOptions = this.buildQueryOptions(params);
    void this.executeQuery(params, queryOptions);
  }
}
